import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrganizations } from '../store/slices/organizationsSlice';
import { risksAPI } from '../services/api';
import Modal from '../components/common/Modal';
import { Loading, EmptyState } from '../components/common';
import { formatDate } from '../utils/formatters';
import { RISK_SEVERITIES, RISK_STATUSES } from '../utils/constants';
import toast from 'react-hot-toast';
import { ExclamationTriangleIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/outline';

const Risks = () => {
  const dispatch = useDispatch();
  const { organizations } = useSelector((state) => state.organizations);
  const { user } = useSelector((state) => state.auth);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [filters, setFilters] = useState({ status: '', severity: '' });
  
  const [formData, setFormData] = useState({
    title: '', description: '', organization: '', severity: 'medium', status: 'identified', result: ''
  });

  useEffect(() => {
    dispatch(fetchOrganizations());
  }, [dispatch]);

  const fetchRisks = async () => {
    try {
      const response = await risksAPI.getAll(filters);
      setRisks(response.data.results || response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRisks(); }, [filters]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedRisk) {
        await risksAPI.update(selectedRisk.id, formData);
        toast.success('Risk yangilandi!');
      } else {
        await risksAPI.create(formData);
        toast.success('Risk qo\'shildi!');
      }
      setShowModal(false);
      resetForm();
      fetchRisks();
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const resetForm = () => {
    setSelectedRisk(null);
    setFormData({ title: '', description: '', organization: '', severity: 'medium', status: 'identified', result: '' });
  };

  const handleEdit = (risk) => {
    setSelectedRisk(risk);
    setFormData({
      title: risk.title, description: risk.description, organization: risk.organization,
      severity: risk.severity, status: risk.status, result: risk.result || ''
    });
    setShowModal(true);
  };

  const getSeverityBadge = (severity) => {
    const styles = { low: 'badge-gray', medium: 'badge-warning', high: 'badge-danger', critical: 'bg-red-600 text-white' };
    return styles[severity] || 'badge-gray';
  };

  const getStatusBadge = (status) => {
    const styles = { identified: 'badge-info', assigned: 'badge-warning', in_progress: 'badge-warning', resolved: 'badge-success' };
    return styles[status] || 'badge-gray';
  };

  const isAdmin = user?.role === 'admin';

  if (loading) return <Loading />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Risklar</h1>
          <p className="text-gray-500">Risk va xavflarni boshqarish</p>
        </div>
        {isAdmin && (
          <button onClick={() => { resetForm(); setShowModal(true); }} className="btn-primary">
            <PlusIcon className="w-5 h-5" /> Yangi risk
          </button>
        )}
      </div>

      <div className="card">
        <div className="flex flex-wrap gap-4">
          <select className="select w-auto" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">Barcha holatlar</option>
            {Object.entries(RISK_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className="select w-auto" value={filters.severity} onChange={(e) => setFilters({ ...filters, severity: e.target.value })}>
            <option value="">Barcha darajalar</option>
            {Object.entries(RISK_SEVERITIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      {risks.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {risks.map((risk) => (
            <div key={risk.id} className="card">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    risk.severity === 'critical' ? 'bg-red-100' :
                    risk.severity === 'high' ? 'bg-orange-100' :
                    risk.severity === 'medium' ? 'bg-yellow-100' : 'bg-gray-100'
                  }`}>
                    <ExclamationTriangleIcon className={`w-6 h-6 ${
                      risk.severity === 'critical' ? 'text-red-600' :
                      risk.severity === 'high' ? 'text-orange-600' :
                      risk.severity === 'medium' ? 'text-yellow-600' : 'text-gray-600'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{risk.title}</h3>
                    <p className="text-sm text-gray-500">{risk.organization_name}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className={`badge ${getSeverityBadge(risk.severity)}`}>{RISK_SEVERITIES[risk.severity]}</span>
                  <span className={`badge ${getStatusBadge(risk.status)}`}>{RISK_STATUSES[risk.status]}</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2">{risk.description}</p>
              {risk.result && (
                <div className="mt-3 p-2 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700"><span className="font-medium">Natija:</span> {risk.result}</p>
                </div>
              )}
              <div className="flex justify-between items-center mt-4 pt-4 border-t">
                <span className="text-xs text-gray-400">{formatDate(risk.created_at)}</span>
                {isAdmin && (
                  <button onClick={() => handleEdit(risk)} className="btn-ghost btn-sm">
                    <PencilIcon className="w-4 h-4" /> Tahrirlash
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState icon={ExclamationTriangleIcon} title="Risklar topilmadi" />
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={selectedRisk ? 'Riskni tahrirlash' : 'Yangi risk'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="input-group">
            <label className="label">Sarlavha *</label>
            <input type="text" className="input" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
          </div>
          <div className="input-group">
            <label className="label">Tavsif *</label>
            <textarea className="textarea" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="input-group">
              <label className="label">Tashkilot</label>
              <select className="select" value={formData.organization} onChange={(e) => setFormData({ ...formData, organization: e.target.value })}>
                <option value="">Tanlang</option>
                {organizations.map(org => <option key={org.id} value={org.id}>{org.short_name || org.name}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label className="label">Darajasi</label>
              <select className="select" value={formData.severity} onChange={(e) => setFormData({ ...formData, severity: e.target.value })}>
                {Object.entries(RISK_SEVERITIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label className="label">Holati</label>
              <select className="select" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                {Object.entries(RISK_STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          {selectedRisk && (
            <div className="input-group">
              <label className="label">Natija</label>
              <textarea className="textarea" value={formData.result} onChange={(e) => setFormData({ ...formData, result: e.target.value })} />
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">{selectedRisk ? 'Saqlash' : 'Qo\'shish'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Risks;
